import { getDocument } from '@atlaskit/browser-apis';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import {
	CypherQueryAtom,
	GraphQLQueryAtom,
	HeadersAtom,
	IsDebugEnabledAtom,
	ParamsAtom,
	SelectedSiteCloudIdAtom,
	TenantModeAtom,
} from '../../../../state/LabsAtoms';
import type { TenantMode } from '../ConfigurationPanel/TenantForm';

export const isLabsDebugEnabledInUrl = (): boolean => {
	const href = getDocument()?.defaultView?.location.href;
	if (!href) {
		return false;
	}

	return new URL(href).searchParams.get('debugEnabled') === 'true';
};

export function useUrlHydration(): void {
	const setTenantMode = useSetAtom(TenantModeAtom);
	const setSelectedSiteCloudId = useSetAtom(SelectedSiteCloudIdAtom);
	const setCypherQuery = useSetAtom(CypherQueryAtom);
	const setGraphQLQuery = useSetAtom(GraphQLQueryAtom);
	const setHeaders = useSetAtom(HeadersAtom);
	const setParams = useSetAtom(ParamsAtom);
	const setDebugEnabled = useSetAtom(IsDebugEnabledAtom);

	useEffect(() => {
		try {
			const url = new URL(window.location.href);
			const q = url.searchParams;

			const tenant = q.get('tenant');
			const tenantMode = q.get('tenantMode');
			const siteCloudId = q.get('siteCloudId');
			const cypher = q.get('cypher');
			const gql = q.get('gql');
			const headersRaw = q.get('headers');
			const paramsRaw = q.get('params');
			setDebugEnabled(isLabsDebugEnabledInUrl());

			// Handle tenant parameter (backward compatibility - may be 'default' or a cloud ID)
			if (tenant) {
				if (tenant === 'default') {
					setTenantMode('default');
				} else {
					// Assume it's a cloud ID for select-site mode
					setTenantMode('select-site');
					setSelectedSiteCloudId(tenant);
				}
			}

			// Handle tenant mode (takes precedence over tenant param)
			if (tenantMode && ['default', 'select-site'].includes(tenantMode)) {
				setTenantMode(tenantMode as TenantMode);
			}

			// Handle selected site cloud ID (takes precedence over tenant param)
			if (siteCloudId) {
				setSelectedSiteCloudId(siteCloudId);
			}
			if (cypher) {
				setCypherQuery(cypher);
			}
			if (gql) {
				setGraphQLQuery(gql);
			}
			if (headersRaw) {
				try {
					const parsed = JSON.parse(headersRaw);
					if (parsed && typeof parsed === 'object') {
						const normalized: Record<string, string> = {};
						for (const k of Object.keys(parsed)) {
							normalized[k] = String((parsed as any)[k]);
						}
						setHeaders(normalized);
					}
				} catch (e) {
					console.error('Failed to parse headers from URL', e);
				}
			}
			if (paramsRaw) {
				try {
					const parsed = JSON.parse(paramsRaw);
					if (parsed && typeof parsed === 'object') {
						setParams(parsed as Record<string, unknown>);
					}
				} catch (e) {
					console.error('Failed to parse params from URL', e);
				}
			}
		} catch (e) {
			console.error('Failed to hydrate from URL', e);
		}
		// This should only run once on mount to hydrate from URL params
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
}
